import { useId, useState } from 'react';
import { can, MIN_REASON, type ChangeKind, type ChangeRequest } from '@urbanrats/ops-core';
import { fmtTime, useCtx } from '../lib.ts';
import { StateTag } from './common.tsx';

const APPROVE: Record<ChangeKind, 'approve.review' | 'approve.config' | 'approve.keyRotation' | 'approve.adjustment' | 'onboarding.manage'> = { REVIEW_DECISION: 'approve.review', CONFIG_CHANGE: 'approve.config', KEY_ROTATION: 'approve.keyRotation', MANUAL_ADJUSTMENT: 'approve.adjustment', ROUND_VOID: 'approve.review', GOODWILL_REFUND: 'approve.adjustment', PARTNER_CONNECTION: 'onboarding.manage', REAL_MONEY: 'onboarding.manage' };

/** Pending and decided change requests with checker controls (maker ≠ checker, reason required). */
export function ChangeRequests({ filter, testId = 'change-requests' }: { filter: (r: ChangeRequest) => boolean; testId?: string }) {
  const { backend, user, refresh } = useCtx();
  const list = backend.requests().filter(filter);
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<string | null>(null);
  const id = useId();
  if (list.length === 0) return <p className="muted">No change requests.</p>;
  const decide = async (r: ChangeRequest, approve: boolean) => {
    const res = await backend.decide(user, r.requestId, approve, reasons[r.requestId] ?? '');
    setMsg(res.ok ? `${r.requestId}: ${res.value.status}${res.value.result ? ` — ${res.value.result}` : ''}` : `${res.error.code}: ${res.error.message}`);
    refresh();
  };
  return (
    <div data-testid={testId}>
      <ul className="requests">
        {list.map((r) => (
          <li key={r.requestId} className="request" data-testid={`cr-${r.requestId}`}>
            <div className="row">
              <strong>{r.requestId}</strong> <StateTag state={r.status} /> <span>{r.summary}</span>
            </div>
            <p className="note">
              Maker {r.maker} ({r.makerRole}) at {fmtTime(r.createdAt)}: “{r.reason}”
            </p>
            {r.checker && (
              <p className="note">
                Checker {r.checker} at {fmtTime(r.decidedAt)}: “{r.decisionReason}”{r.result ? ` → ${r.result}` : ''}
              </p>
            )}
            {r.status === 'PENDING' && can(user, APPROVE[r.kind]) && (
              <div className="decide">
                <label htmlFor={`${id}-${r.requestId}`}>Checker reason (min {MIN_REASON} chars)</label>
                <input id={`${id}-${r.requestId}`} value={reasons[r.requestId] ?? ''} onChange={(e) => setReasons({ ...reasons, [r.requestId]: e.target.value })} data-testid="checker-reason" />
                <button type="button" className="btn primary" onClick={() => void decide(r, true)} data-testid="approve">
                  Approve
                </button>
                <button type="button" className="btn" onClick={() => void decide(r, false)} data-testid="reject">
                  Reject
                </button>
                {r.maker === user.userId && <p className="note">You made this request: another user must approve it.</p>}
              </div>
            )}
          </li>
        ))}
      </ul>
      {msg && (
        <p role="status" className="status-msg" data-testid="decision-msg">
          {msg}
        </p>
      )}
    </div>
  );
}
